import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  TableSortLabel,
  Collapse,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  CheckCircle as ConfirmedIcon,
  HourglassEmpty as UnconfirmedIcon,
  OpenInNew as ExternalLinkIcon,
} from '@mui/icons-material';
import { crlApi } from '../../services/api';

const scoreColors = {
  easy: '#4caf50',
  moderate: '#ff9800',
  difficult: '#f44336',
  veryDifficult: '#e91e63',
};

const deficiencyCategories = [
  { key: 'has_clinical_efficacy_issue', label: 'Clinical Efficacy', color: '#e53935' },
  { key: 'has_clinical_safety_issue', label: 'Clinical Safety', color: '#d32f2f' },
  { key: 'has_product_quality_cmc_issue', label: 'Product Quality/CMC', color: '#7b1fa2' },
  { key: 'has_facility_inspection_issue', label: 'Facility Inspection', color: '#512da8' },
  { key: 'has_nonclinical_issue', label: 'Nonclinical', color: '#1976d2' },
  { key: 'has_clinical_pharmacology_issue', label: 'Clinical Pharmacology', color: '#0288d1' },
  { key: 'has_device_human_factors_issue', label: 'Device/Human Factors', color: '#00796b' },
  { key: 'has_labeling_issue', label: 'Labeling', color: '#689f38' },
];

function LetterList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [letters, setLetters] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedLetters, setExpandedLetters] = useState({});
  const [loadingLetters, setLoadingLetters] = useState({});

  // Modal state for viewing letter text
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);

  // Filters
  const [company, setCompany] = useState(searchParams.get('company') || '');
  const [minScore, setMinScore] = useState(searchParams.get('min_score') || '');
  const [maxScore, setMaxScore] = useState(searchParams.get('max_score') || '');
  const [hasNewTrial, setHasNewTrial] = useState(searchParams.get('has_new_trial') === 'true');
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Sorting
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'date');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sort_order') || 'desc');

  useEffect(() => {
    loadLetters();
  }, [page, rowsPerPage, sortBy, sortOrder]);

  const loadLetters = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (company) params.company = company;
      if (minScore) params.min_score = minScore;
      if (maxScore) params.max_score = maxScore;
      if (hasNewTrial) params.has_new_trial = 'true';
      if (sourceFilter) params.source = sourceFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await crlApi.getLetters(params);
      setLetters(response.data.letters);
      setTotal(response.data.total);

      // Update URL params
      const newParams = new URLSearchParams();
      if (page > 0) newParams.set('page', page);
      if (company) newParams.set('company', company);
      if (minScore) newParams.set('min_score', minScore);
      if (maxScore) newParams.set('max_score', maxScore);
      if (hasNewTrial) newParams.set('has_new_trial', 'true');
      if (sourceFilter) newParams.set('source', sourceFilter);
      if (statusFilter) newParams.set('status', statusFilter);
      if (sortBy !== 'date') newParams.set('sort_by', sortBy);
      if (sortOrder !== 'desc') newParams.set('sort_order', sortOrder);
      setSearchParams(newParams);
    } catch (err) {
      setError('Failed to load letters. Make sure the API is running.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (e) => {
    e.preventDefault();
    setPage(0);
    loadLetters();
  };

  const clearFilters = () => {
    setCompany('');
    setMinScore('');
    setMaxScore('');
    setHasNewTrial(false);
    setSourceFilter('');
    setStatusFilter('');
    setSortBy('date');
    setSortOrder('desc');
    setPage(0);
  };

  const handleSort = (column) => {
    const isAsc = sortBy === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(column);
    setPage(0);
  };

  const handleExpandRow = async (applicationNumber) => {
    const isExpanded = expandedRows[applicationNumber];
    setExpandedRows({ ...expandedRows, [applicationNumber]: !isExpanded });

    // Load letters for this application if not already loaded
    if (!isExpanded && !expandedLetters[applicationNumber]) {
      setLoadingLetters({ ...loadingLetters, [applicationNumber]: true });
      try {
        const response = await crlApi.getApplicationLetters(applicationNumber);
        setExpandedLetters({ ...expandedLetters, [applicationNumber]: response.data.letters });
      } catch (err) {
        console.error('Error loading application letters:', err);
      } finally {
        setLoadingLetters({ ...loadingLetters, [applicationNumber]: false });
      }
    }
  };

  const getScoreColor = (score) => {
    if (!score) return '#999';
    if (score < 2) return scoreColors.easy;
    if (score < 3) return scoreColors.moderate;
    if (score < 4) return scoreColors.difficult;
    return scoreColors.veryDifficult;
  };

  const getDifficultyLabel = (score) => {
    if (!score) return '';
    if (score < 2) return 'Low';
    if (score < 3) return 'Moderate';
    if (score < 4) return 'High';
    return 'Very High';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getActiveDeficiencies = (letter) => {
    return deficiencyCategories.filter((cat) => letter[cat.key]);
  };

  const handleOpenTextModal = (letter, e) => {
    e.stopPropagation();
    setSelectedLetter(letter);
    setTextModalOpen(true);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        CRL Letter Explorer
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={handleFilter} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by company..."
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            type="number"
            placeholder="Min score"
            value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            inputProps={{ step: 0.1, min: 1, max: 5 }}
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            type="number"
            placeholder="Max score"
            value={maxScore}
            onChange={(e) => setMaxScore(e.target.value)}
            inputProps={{ step: 0.1, min: 1, max: 5 }}
            sx={{ width: 120 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={hasNewTrial}
                onChange={(e) => setHasNewTrial(e.target.checked)}
                size="small"
              />
            }
            label="New Trial Required"
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={sourceFilter}
              label="Source"
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="FDA">FDA</MenuItem>
              <MenuItem value="8K">8-K</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="unconfirmed">Unconfirmed</MenuItem>
            </Select>
          </FormControl>
          <Button type="submit" variant="contained" size="small">
            Apply
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={clearFilters}>
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Results */}
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50}></TableCell>
                  <TableCell>Application</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'date'}
                      direction={sortBy === 'date' ? sortOrder : 'asc'}
                      onClick={() => handleSort('date')}
                    >
                      Latest Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'score'}
                      direction={sortBy === 'score' ? sortOrder : 'asc'}
                      onClick={() => handleSort('score')}
                    >
                      AI Difficulty
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>CRL Count</TableCell>
                  <TableCell>Deficiencies</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {letters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">No letters found matching your criteria.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  letters.map((letter) => (
                    <React.Fragment key={letter.application_number}>
                      {/* Main Row */}
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          '& > *': { borderBottom: expandedRows[letter.application_number] ? 'none' : undefined },
                          bgcolor: letter.status === 'unconfirmed' ? 'rgba(255, 152, 0, 0.08)' : undefined,
                          '&:hover': {
                            bgcolor: letter.status === 'unconfirmed' ? 'rgba(255, 152, 0, 0.15)' : undefined,
                          },
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleExpandRow(letter.application_number)}
                          >
                            {expandedRows[letter.application_number] ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell onClick={() => navigate(`/crl/letters/${letter.letter_id}`)}>
                          <Typography color="primary" fontWeight="medium">
                            {letter.application_number || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={() => navigate(`/crl/letters/${letter.letter_id}`)}>
                          {letter.company_name || '-'}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/crl/letters/${letter.letter_id}`)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={letter.source === '8K' ? '8-K' : 'FDA'}
                              size="small"
                              sx={{
                                bgcolor: letter.source === '8K' ? '#ff9800' : '#2196f3',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                              }}
                            />
                            <Tooltip title={letter.status === 'confirmed' ? 'Confirmed by FDA' : 'Awaiting FDA confirmation'}>
                              {letter.status === 'confirmed' ? (
                                <ConfirmedIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                              ) : (
                                <UnconfirmedIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                              )}
                            </Tooltip>
                            {letter.sec_8k_link && (
                              <Tooltip title="View SEC Filing">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(letter.sec_8k_link, '_blank');
                                  }}
                                  sx={{ p: 0.25 }}
                                >
                                  <ExternalLinkIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell onClick={() => navigate(`/crl/letters/${letter.letter_id}`)}>
                          {formatDate(letter.latest_date || letter.letter_date)}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/crl/letters/${letter.letter_id}`)}>
                          {letter.mean_difficulty_score ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={letter.mean_difficulty_score.toFixed(1)}
                                size="small"
                                sx={{
                                  bgcolor: getScoreColor(letter.mean_difficulty_score),
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {getDifficultyLabel(letter.mean_difficulty_score)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Processing...</Typography>
                          )}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/crl/letters/${letter.letter_id}`)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={letter.letter_count || 1}
                              size="small"
                              color={letter.letter_count > 1 ? 'warning' : 'default'}
                            />
                            {letter.any_new_trial_required === 1 && (
                              <Chip label="New Trial" size="small" color="error" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {getActiveDeficiencies(letter).slice(0, 3).map((cat) => (
                              <Chip
                                key={cat.key}
                                label={cat.label}
                                size="small"
                                sx={{ bgcolor: cat.color, color: 'white', fontSize: '0.7rem' }}
                              />
                            ))}
                            {getActiveDeficiencies(letter).length > 3 && (
                              <Chip
                                label={`+${getActiveDeficiencies(letter).length - 3}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expandable Detail Row */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={expandedRows[letter.application_number]} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 3, bgcolor: 'grey.50' }}>
                              {loadingLetters[letter.application_number] ? (
                                <CircularProgress size={24} />
                              ) : expandedLetters[letter.application_number] ? (
                                <>
                                  <Typography variant="subtitle2" gutterBottom>
                                    All CRL Letters for {letter.application_number} ({expandedLetters[letter.application_number].length} total)
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Source</TableCell>
                                        <TableCell>Difficulty</TableCell>
                                        <TableCell>Timeline</TableCell>
                                        <TableCell>Deficiency Categories</TableCell>
                                        <TableCell>Summary</TableCell>
                                        <TableCell>Text</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {expandedLetters[letter.application_number].map((subLetter) => (
                                        <TableRow
                                          key={subLetter.letter_id}
                                          hover
                                          sx={{
                                            cursor: 'pointer',
                                            bgcolor: subLetter.status === 'unconfirmed' ? 'rgba(255, 152, 0, 0.1)' : undefined,
                                          }}
                                          onClick={() => navigate(`/crl/letters/${subLetter.letter_id}`)}
                                        >
                                          <TableCell>{formatDate(subLetter.letter_date)}</TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Chip
                                                label={subLetter.source === '8K' ? '8-K' : 'FDA'}
                                                size="small"
                                                sx={{
                                                  bgcolor: subLetter.source === '8K' ? '#ff9800' : '#2196f3',
                                                  color: 'white',
                                                  fontWeight: 'bold',
                                                  fontSize: '0.65rem',
                                                }}
                                              />
                                              {subLetter.status === 'confirmed' ? (
                                                <ConfirmedIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                                              ) : (
                                                <UnconfirmedIcon sx={{ color: '#ff9800', fontSize: 16 }} />
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            {subLetter.mean_difficulty_score ? (
                                              <Chip
                                                label={subLetter.mean_difficulty_score.toFixed(1)}
                                                size="small"
                                                sx={{
                                                  bgcolor: getScoreColor(subLetter.mean_difficulty_score),
                                                  color: 'white',
                                                  fontWeight: 'bold',
                                                }}
                                              />
                                            ) : (
                                              '-'
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {subLetter.estimated_months_expected
                                              ? `${subLetter.estimated_months_expected} mo`
                                              : '-'}
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                              {getActiveDeficiencies(subLetter).map((cat) => (
                                                <Chip
                                                  key={cat.key}
                                                  label={cat.label}
                                                  size="small"
                                                  sx={{ bgcolor: cat.color, color: 'white', fontSize: '0.65rem' }}
                                                />
                                              ))}
                                              {subLetter.new_clinical_trial_required && (
                                                <Chip label="New Trial" size="small" color="error" />
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell sx={{ maxWidth: 200 }}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                              }}
                                            >
                                              {subLetter.primary_deficiency_summary || '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            {subLetter.raw_text && (
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => handleOpenTextModal(subLetter, e)}
                                                title="View Letter Text"
                                              >
                                                <DescriptionIcon />
                                              </IconButton>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </>
                              ) : (
                                <Typography color="text.secondary">Loading...</Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>
      )}

      {/* Letter Text Modal */}
      <Dialog
        open={textModalOpen}
        onClose={() => setTextModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Complete Response Letter</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedLetter?.application_number} - {formatDate(selectedLetter?.letter_date)}
            </Typography>
          </Box>
          <IconButton onClick={() => setTextModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              p: 2,
              bgcolor: '#fafafa',
              borderRadius: 1,
            }}
          >
            {selectedLetter?.raw_text || 'No letter text available.'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LetterList;
